import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Base from '../components/Base';
import API_BASE_URL from '../config/api';
import './MyCoupons.css';

const MyCoupons = () => {
    const [coupons, setCoupons] = useState([]);
    const [stats, setStats] = useState({ total_coupons: 0, active_coupons: 0, total_uses: 0 });
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // Form state
    const [formData, setFormData] = useState({
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        min_order_amount: 0,
        max_discount_amount: '',
        usage_limit: '',
        valid_from: '',
        valid_until: '',
        is_active: true,
        description: ''
    });

    useEffect(() => {
        fetchCoupons();
    }, [statusFilter]);

    const fetchCoupons = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const url = statusFilter === 'all'
                ? `${API_BASE_URL}/api/coupons/`
                : `${API_BASE_URL}/api/coupons/?status=${statusFilter}`;

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setCoupons(response.data.data.coupons);
                setStats(response.data.data.stats);
            }
        } catch (error) {
            console.error('Error fetching coupons:', error);
            Swal.fire('Error', 'Failed to load coupons', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateCode = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.post(
                `${API_BASE_URL}/api/coupons/generate-code/`,
                { length: 8 },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setFormData({ ...formData, code: response.data.data.code });
            }
        } catch (error) {
            Swal.fire('Error', 'Failed to generate code', 'error');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const token = localStorage.getItem('access_token');
            const url = editingCoupon
                ? `${API_BASE_URL}/api/coupons/${editingCoupon.id}/`
                : `${API_BASE_URL}/api/coupons/`;

            const method = editingCoupon ? 'put' : 'post';

            // Clean up data - convert empty strings to null for numeric fields
            const cleanedData = {
                ...formData,
                min_order_amount: formData.min_order_amount === '' ? 0 : parseFloat(formData.min_order_amount),
                max_discount_amount: formData.max_discount_amount === '' ? null : parseFloat(formData.max_discount_amount),
                usage_limit: formData.usage_limit === '' ? null : parseInt(formData.usage_limit),
                discount_value: parseFloat(formData.discount_value),
                valid_until: formData.valid_until === '' ? null : formData.valid_until
            };

            const response = await axios[method](url, cleanedData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                Swal.fire('Success', response.data.message, 'success');
                setShowModal(false);
                resetForm();
                fetchCoupons();
            }
        } catch (error) {
            const errorMsg = error.response?.data?.errors
                ? Object.values(error.response.data.errors).flat().join(', ')
                : 'Failed to save coupon';
            Swal.fire('Error', errorMsg, 'error');
        }
    };

    const handleToggleStatus = async (couponId) => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.patch(
                `${API_BASE_URL}/api/coupons/${couponId}/toggle/`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                Swal.fire('Success', response.data.message, 'success');
                fetchCoupons();
            }
        } catch (error) {
            Swal.fire('Error', 'Failed to toggle status', 'error');
        }
    };

    const handleDelete = async (couponId, code) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Delete coupon "${code}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('access_token');
                await axios.delete(
                    `${API_BASE_URL}/api/coupons/${couponId}/`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                Swal.fire('Deleted!', 'Coupon deleted successfully', 'success');
                fetchCoupons();
            } catch (error) {
                Swal.fire('Error', 'Failed to delete coupon', 'error');
            }
        }
    };

    const handleEdit = (coupon) => {
        setEditingCoupon(coupon);
        setFormData({
            code: coupon.code,
            discount_type: coupon.discount_type,
            discount_value: coupon.discount_value,
            min_order_amount: coupon.min_order_amount || 0,
            max_discount_amount: coupon.max_discount_amount || '',
            usage_limit: coupon.usage_limit || '',
            valid_from: coupon.valid_from ? coupon.valid_from.substring(0, 16) : '',
            valid_until: coupon.valid_until ? coupon.valid_until.substring(0, 16) : '',
            is_active: coupon.is_active,
            description: coupon.description || ''
        });
        setShowModal(true);
    };

    const handleCopyCode = (code) => {
        navigator.clipboard.writeText(code);
        Swal.fire({
            title: 'Copied!',
            text: `Coupon code "${code}" copied to clipboard`,
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
        });
    };

    const handleBulkDeactivate = async () => {
        const result = await Swal.fire({
            title: 'Deactivate expired coupons?',
            text: 'This will deactivate all expired coupons',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, deactivate'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('access_token');
                const response = await axios.post(
                    `${API_BASE_URL}/api/coupons/bulk-deactivate/`,
                    {},
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.success) {
                    Swal.fire('Success', response.data.message, 'success');
                    fetchCoupons();
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to deactivate coupons', 'error');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            code: '',
            discount_type: 'percentage',
            discount_value: '',
            min_order_amount: 0,
            max_discount_amount: '',
            usage_limit: '',
            valid_from: '',
            valid_until: '',
            is_active: true,
            description: ''
        });
        setEditingCoupon(null);
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: { class: 'badge-active', text: 'Active', icon: '🟢' },
            inactive: { class: 'badge-inactive', text: 'Inactive', icon: '⚫' },
            expired: { class: 'badge-expired', text: 'Expired', icon: '🔴' },
            limit_reached: { class: 'badge-limit', text: 'Limit Reached', icon: '🟠' },
            scheduled: { class: 'badge-scheduled', text: 'Scheduled', icon: '🔵' }
        };
        const badge = badges[status] || badges.inactive;
        return (
            <span className={`status-badge ${badge.class}`}>
                {badge.icon} {badge.text}
            </span>
        );
    };

    const filteredCoupons = coupons.filter(coupon =>
        coupon.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (coupon.description && coupon.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <Base Content={
            <div className="my-coupons-container">
            <div className="coupons-header">
                <h1>🎫 My Coupons</h1>
                <button className="btn-create" onClick={() => { resetForm(); setShowModal(true); }}>
                    + Create Coupon
                </button>
            </div>

            {/* Stats Cards */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon">📊</div>
                    <div className="stat-info">
                        <div className="stat-label">Total Coupons</div>
                        <div className="stat-value">{stats.total_coupons}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">✅</div>
                    <div className="stat-info">
                        <div className="stat-label">Active Coupons</div>
                        <div className="stat-value">{stats.active_coupons}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">📈</div>
                    <div className="stat-info">
                        <div className="stat-label">Total Uses</div>
                        <div className="stat-value">{stats.total_uses}</div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="filters-bar">
                <input
                    type="text"
                    placeholder="🔍 Search coupons..."
                    className="search-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <select
                    className="filter-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <option value="all">All Coupons</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="expired">Expired</option>
                </select>
                <button className="btn-bulk" onClick={handleBulkDeactivate}>
                    Deactivate Expired
                </button>
            </div>

            {/* Coupons Table */}
            <div className="coupons-table-container">
                {loading ? (
                    <div className="loading">Loading...</div>
                ) : filteredCoupons.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">🎫</div>
                        <h3>No coupons found</h3>
                        <p>Create your first coupon to get started!</p>
                    </div>
                ) : (
                    <table className="coupons-table">
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Discount</th>
                                <th>Status</th>
                                <th>Used</th>
                                <th>Valid Until</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCoupons.map(coupon => (
                                <tr key={coupon.id}>
                                    <td>
                                        <div className="code-cell">
                                            <strong>{coupon.code}</strong>
                                            <button
                                                className="btn-copy"
                                                onClick={() => handleCopyCode(coupon.code)}
                                                title="Copy code"
                                            >
                                                📋
                                            </button>
                                        </div>
                                        {coupon.description && (
                                            <div className="coupon-description">{coupon.description}</div>
                                        )}
                                        {coupon.min_order_amount > 0 && (
                                            <div className="coupon-info">Min: ₹{coupon.min_order_amount}</div>
                                        )}
                                    </td>
                                    <td>
                                        <span className="discount-value">{coupon.discount_display}</span>
                                        {coupon.max_discount_amount && (
                                            <div className="max-cap">Max: ₹{coupon.max_discount_amount}</div>
                                        )}
                                    </td>
                                    <td>{getStatusBadge(coupon.status)}</td>
                                    <td>
                                        <div className="usage-info">
                                            {coupon.times_used}
                                            {coupon.usage_limit && ` / ${coupon.usage_limit}`}
                                        </div>
                                    </td>
                                    <td>
                                        {coupon.valid_until
                                            ? new Date(coupon.valid_until).toLocaleDateString()
                                            : 'Never expires'}
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                className="btn-toggle"
                                                onClick={() => handleToggleStatus(coupon.id)}
                                                title={coupon.is_active ? 'Deactivate' : 'Activate'}
                                            >
                                                {coupon.is_active ? '⏸' : '▶'}
                                            </button>
                                            <button
                                                className="btn-edit"
                                                onClick={() => handleEdit(coupon)}
                                                title="Edit"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                className="btn-delete"
                                                onClick={() => handleDelete(coupon.id, coupon.code)}
                                                title="Delete"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}</h2>
                            <button className="btn-close" onClick={() => { setShowModal(false); resetForm(); }}>×</button>
                        </div>

                        <form onSubmit={handleSubmit} className="coupon-form">
                            {/* Coupon Code */}
                            <div className="form-section">
                                <h3>Step 1: Coupon Code</h3>
                                <div className="form-group">
                                    <label>Coupon Code *</label>
                                    <div className="input-with-button">
                                        <input
                                            type="text"
                                            value={formData.code}
                                            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                            placeholder="SUMMER20"
                                            required
                                            maxLength={20}
                                        />
                                        <button
                                            type="button"
                                            className="btn-generate"
                                            onClick={handleGenerateCode}
                                        >
                                            Generate Code
                                        </button>
                                    </div>
                                    <small>Alphanumeric, 3-20 characters</small>
                                </div>

                                <div className="form-group">
                                    <label>Description (Optional)</label>
                                    <input
                                        type="text"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Summer Sale 2025"
                                    />
                                </div>
                            </div>

                            {/* Discount Configuration */}
                            <div className="form-section">
                                <h3>Step 2: Discount Configuration</h3>
                                <div className="form-group">
                                    <label>Discount Type *</label>
                                    <div className="radio-group">
                                        <label className="radio-label">
                                            <input
                                                type="radio"
                                                name="discount_type"
                                                value="percentage"
                                                checked={formData.discount_type === 'percentage'}
                                                onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                                            />
                                            Percentage (%)
                                        </label>
                                        <label className="radio-label">
                                            <input
                                                type="radio"
                                                name="discount_type"
                                                value="fixed"
                                                checked={formData.discount_type === 'fixed'}
                                                onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                                            />
                                            Fixed Amount (₹)
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Discount Value *</label>
                                    <input
                                        type="number"
                                        value={formData.discount_value}
                                        onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                                        placeholder={formData.discount_type === 'percentage' ? '20' : '100'}
                                        required
                                        min="0"
                                        max={formData.discount_type === 'percentage' ? '100' : undefined}
                                        step="0.01"
                                    />
                                    <small>
                                        {formData.discount_type === 'percentage'
                                            ? `Customers get ${formData.discount_value || 0}% off`
                                            : `Customers get ₹${formData.discount_value || 0} off`}
                                    </small>
                                </div>
                            </div>

                            {/* Restrictions */}
                            <div className="form-section">
                                <h3>Step 3: Restrictions (Optional)</h3>
                                <div className="form-group">
                                    <label>Minimum Order Amount</label>
                                    <input
                                        type="number"
                                        value={formData.min_order_amount}
                                        onChange={(e) => setFormData({ ...formData, min_order_amount: e.target.value })}
                                        placeholder="500"
                                        min="0"
                                        step="0.01"
                                    />
                                    <small>Leave 0 for no minimum</small>
                                </div>

                                {formData.discount_type === 'percentage' && (
                                    <div className="form-group">
                                        <label>Maximum Discount Cap</label>
                                        <input
                                            type="number"
                                            value={formData.max_discount_amount}
                                            onChange={(e) => setFormData({ ...formData, max_discount_amount: e.target.value })}
                                            placeholder="200"
                                            min="0"
                                            step="0.01"
                                        />
                                        <small>Limits maximum discount amount</small>
                                    </div>
                                )}

                                <div className="form-group">
                                    <label>Usage Limit</label>
                                    <input
                                        type="number"
                                        value={formData.usage_limit}
                                        onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                                        placeholder="100"
                                        min="1"
                                    />
                                    <small>Leave empty for unlimited uses</small>
                                </div>
                            </div>

                            {/* Validity Period */}
                            <div className="form-section">
                                <h3>Step 4: Validity Period</h3>
                                <div className="form-group">
                                    <label>Valid From *</label>
                                    <input
                                        type="datetime-local"
                                        value={formData.valid_from}
                                        onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Valid Until</label>
                                    <input
                                        type="datetime-local"
                                        value={formData.valid_until}
                                        onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                                    />
                                    <small>Leave empty for no expiry</small>
                                </div>

                                <div className="form-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={formData.is_active}
                                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                        />
                                        Active (Customers can use immediately)
                                    </label>
                                </div>
                            </div>

                            {/* Preview */}
                            {formData.code && formData.discount_value && (
                                <div className="coupon-preview">
                                    <h3>Preview</h3>
                                    <div className="preview-card">
                                        <div className="preview-code">🎫 {formData.code}</div>
                                        <div className="preview-text">
                                            Get {formData.discount_type === 'percentage' ? `${formData.discount_value}%` : `₹${formData.discount_value}`} off
                                            {formData.min_order_amount > 0 && ` on orders above ₹${formData.min_order_amount}`}
                                        </div>
                                        {formData.max_discount_amount && (
                                            <div className="preview-cap">Max discount: ₹{formData.max_discount_amount}</div>
                                        )}
                                        {formData.valid_until && (
                                            <div className="preview-validity">
                                                Valid till: {new Date(formData.valid_until).toLocaleDateString()}
                                            </div>
                                        )}
                                        {formData.usage_limit && (
                                            <div className="preview-limit">Limited to first {formData.usage_limit} customers</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="form-actions">
                                <button type="button" className="btn-cancel" onClick={() => { setShowModal(false); resetForm(); }}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn-submit">
                                    {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            </div>
        } />
    );
};

export default MyCoupons;
